import { useEffect, useRef, useState } from "react";
import { TrendingUp, Sparkles } from "lucide-react";
import api from "@/lib/api";
import ProUpsell from "@/components/ProUpsell";

export default function HiveTrendsTab({ hiveId, highlightMonth }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upsell, setUpsell] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/trends/hive/${hiveId}`, { params: { months: 12 } });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (e.response?.status === 402) {
          if (!cancelled) setUpsell(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [hiveId]);

  if (loading) return <div className="text-sm text-stone-500 py-6" data-testid="trends-loading">Loading trends…</div>;
  if (upsell) {
    return (
      <div data-testid="trends-upsell">
        <ProUpsell open={upsell} onOpenChange={setUpsell} feature="trends" />
        <div className="text-center py-12 border border-dashed border-stone-300 rounded-2xl">
          <Sparkles className="w-6 h-6 text-amber-500 mx-auto mb-2" />
          <p className="text-stone-600 text-sm">Health Trends is a Pro feature.</p>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const { series, totals } = data;

  return (
    <div className="space-y-6" data-testid="trends-tab">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Inspections" value={totals.inspections} />
        <Stat label="Harvest (lb)" value={totals.harvests_lb} />
        <Stat label="Diagnoses" value={totals.diagnoses} />
        <Stat label="Treatments" value={totals.treatments} />
        <Stat label="Feedings" value={totals.feedings} />
      </div>

      <Chart
        title="Inspections per month"
        series={series}
        accessor={(s) => s.inspections}
        accent="#0EA5E9"
        highlightMonth={highlightMonth}
      />
      <Chart
        title="Harvest (lb) per month"
        series={series}
        accessor={(s) => s.harvests_lb}
        accent="#F59E0B"
        highlightMonth={highlightMonth}
      />
      <Chart
        title="Average mood (1=irritated, 4=calm)"
        series={series}
        accessor={(s) => s.avg_mood ?? 0}
        accent="#10B981"
        domain={[0, 4]}
        highlightMonth={highlightMonth}
      />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-stone-200 rounded-xl p-3" data-testid={`trend-stat-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500 font-semibold">{label}</div>
      <div className="font-display text-2xl font-bold text-stone-900 tabular-nums mt-1">{value}</div>
    </div>
  );
}

/** Simple SVG bar chart — no external deps. */
function Chart({ title, series, accessor, accent, domain, highlightMonth }) {
  const containerRef = useRef(null);
  // Scroll the first highlighted chart into view + add a brief pulse so the user
  // immediately sees which month they tapped through to.
  useEffect(() => {
    if (highlightMonth && containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightMonth]);

  if (!series.length) {
    return (
      <div className="border border-stone-200 rounded-2xl p-5">
        <div className="font-medium text-stone-900 mb-2 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-stone-400" /> {title}
        </div>
        <div className="text-sm text-stone-500 py-8 text-center">No data yet.</div>
      </div>
    );
  }
  const vals = series.map(accessor);
  const max = domain ? domain[1] : Math.max(1, ...vals);
  const w = 800, h = 160, padX = 24, padY = 16;
  const bw = (w - padX * 2) / series.length - 4;
  return (
    <div
      ref={containerRef}
      className={`border rounded-2xl p-5 transition-shadow ${
        highlightMonth && series.some((s) => s.month === highlightMonth)
          ? "border-amber-300 shadow-[0_0_0_3px_rgba(252,211,77,0.35)]"
          : "border-stone-200"
      }`}
      data-testid={`trends-chart-${title.toLowerCase().replace(/[^a-z]/g, "-")}`}
    >
      <div className="font-medium text-stone-900 mb-3 flex items-center gap-2">
        <TrendingUp className="w-4 h-4" style={{ color: accent }} /> {title}
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-40">
        {series.map((s, i) => {
          const v = accessor(s) || 0;
          const barH = ((v / max) * (h - padY * 2)) || 0;
          const x = padX + i * ((w - padX * 2) / series.length);
          const isHighlight = s.month === highlightMonth;
          return (
            <g key={s.month}>
              <rect
                x={x + 2}
                y={h - padY - barH}
                width={bw}
                height={barH}
                fill={accent}
                opacity={isHighlight ? 1 : 0.85}
                stroke={isHighlight ? "#1c1917" : "none"}
                strokeWidth={isHighlight ? 1.5 : 0}
                rx={2}
              />
              <text
                x={x + bw / 2 + 2}
                y={h - 2}
                textAnchor="middle"
                fontSize="9"
                fill={isHighlight ? "#1c1917" : "#78716C"}
                fontWeight={isHighlight ? 700 : 400}
              >
                {s.month.slice(2)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
